import { Fragment, type ReactNode } from 'react';

export interface Column<T> {
  key: string;
  header: string;
  render: (item: T) => ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyFn: (item: T) => string | number;
  onRowClick?: (item: T) => void;
  /** Optional second row under a data row (e.g. progress UI). Return null to omit. */
  renderExpanded?: (item: T) => ReactNode | null;
  rowClassName?: (item: T) => string | undefined;
  className?: string;
}

export function DataTable<T>({
  columns,
  data,
  keyFn,
  onRowClick,
  renderExpanded,
  rowClassName,
  className = '',
}: DataTableProps<T>) {
  return (
    <div className={`min-w-0 overflow-x-auto ${className}`}>
      <table className='min-w-full divide-y divide-neutral-100 text-left text-sm'>
        <thead className='bg-neutral-25 text-neutral-600'>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className={`whitespace-nowrap p-3 font-medium sm:px-4 ${col.className ?? ''}`}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className='divide-y divide-neutral-100 bg-white'>
          {data.map((item) => {
            const key = keyFn(item);
            const expanded = renderExpanded?.(item) ?? null;
            const extraRowClass = rowClassName?.(item) ?? '';
            return (
              <Fragment key={key}>
                <tr
                  className={`hover:bg-neutral-25 ${onRowClick ? 'cursor-pointer' : ''} ${extraRowClass}`}
                  onClick={onRowClick ? () => onRowClick(item) : undefined}
                >
                  {columns.map((col) => (
                    <td key={col.key} className={`p-3 align-top sm:px-4 ${col.className ?? ''}`}>
                      {col.render(item)}
                    </td>
                  ))}
                </tr>
                {expanded != null && (
                  <tr className='bg-neutral-25/60'>
                    <td colSpan={columns.length} className='px-3 pb-4 pt-3 sm:px-4'>
                      {expanded}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
